// Confetti — full-screen celebratory burst overlay (first feed unlock, level-up,
// session complete, onboarding "Nice work"). Shape ported from iOS
// client-native/.../ConfettiBurst.tsx: a center burst (~38% from top) of ~50
// particles in a 12-color palette, with a delayed second wave, after a 500ms
// base delay. Uses the global `canvas-confetti` API, which creates + sizes its
// own full-screen canvas — the scoped `confetti.create(canvas)` path silently
// drew nothing (canvas never sized / rendered) under this Vite/PWA build.
import { FC, useEffect, useRef } from "react";
import confetti from "canvas-confetti";

// iOS ConfettiBurst festive palette (12 colors).
const CONFETTI_COLORS = [
  "#FFD700", "#FF6B6B", "#4ECDC4", "#45B7D1",
  "#96CEB4", "#FFEAA7", "#DDA0DD", "#FF8C00",
  "#FFFFFF", "#87CEEB", "#FF69B4", "#7B68EE",
];

// iOS BASE_DELAY before the burst kicks off.
const BASE_DELAY = 500;

export interface ConfettiProps {
  /** When true, fires the burst once. Toggling false→true re-fires. */
  active: boolean;
  /** Total visible duration before onDone fires. Defaults to 2200ms (matches iOS). */
  durationMs?: number;
  /** Called after the burst finishes. */
  onDone?: () => void;
}

const Confetti: FC<ConfettiProps> = ({ active, durationMs = 2200, onDone }) => {
  // Hold callback + duration in refs so the burst effect depends ONLY on
  // `active` (a fresh onDone/durationMs closure mid-burst must not re-run it).
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const durationRef = useRef(durationMs);
  durationRef.current = durationMs;

  useEffect(() => {
    if (!active) return;
    // No "already fired" ref guard: it broke StrictMode's mount→cleanup→mount
    // (the cleanup cleared the timers, then the guard skipped rescheduling, so
    // the burst never fired in dev). The effect keys only on `active`, so it
    // already runs exactly once per false→true transition.
    const dur = durationRef.current;

    const shared = {
      origin: { x: 0.5, y: 0.38 }, // iOS BURST_X / BURST_Y
      colors: CONFETTI_COLORS,
      startVelocity: 45,
      gravity: 1.1,
      scalar: 1,
      ticks: Math.round((dur / 1000) * 60),
      disableForReducedMotion: true,
      zIndex: 9999,
    } as const;

    // Wave 1: full 360° burst after the base delay.
    const wave1Timer = window.setTimeout(() => {
      confetti({ ...shared, particleCount: 35, spread: 360 });
    }, BASE_DELAY);

    // Wave 2: delayed second pop (~400ms later).
    const wave2Timer = window.setTimeout(() => {
      confetti({ ...shared, particleCount: 15, spread: 360, startVelocity: 38 });
    }, BASE_DELAY + 400);

    const doneTimer = window.setTimeout(() => {
      onDoneRef.current?.();
    }, BASE_DELAY + dur);

    return () => {
      window.clearTimeout(wave1Timer);
      window.clearTimeout(wave2Timer);
      window.clearTimeout(doneTimer);
      confetti.reset();
    };
  }, [active]);

  return null;
};

export default Confetti;
